import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  Phone, PhoneIncoming, PhoneOutgoing, PhoneOff, Clock, Mic, MicOff,
  Sparkles, Activity, User, Search, Volume2, AlertCircle, CheckCircle2,
  Play, Pause, SkipForward, SkipBack, FastForward, Rewind, Cloud, CloudOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calling = () => {
  const { token } = useAuth();
  const [calls, setCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [elevenLabsStatus, setElevenLabsStatus] = useState(null);
  
  // Call state
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle, dialing, connected, ended
  const [currentCall, setCurrentCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  
  // Dialer state
  const [showDialer, setShowDialer] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const callTimerRef = useRef(null);
  const audioRef = useRef(null);
  const [playingRecordingSid, setPlayingRecordingSid] = useState(null);
  
  // Audio player state
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [currentAudioCall, setCurrentAudioCall] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [syncingCallId, setSyncingCallId] = useState(null);

  useEffect(() => {
    fetchData();
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, []);

  // Progressive — calls / contacts / integrations status each land
  // independently so a slow third-party status check doesn't block.
  const fetchData = () => {
    const tasks = [
      axios.get(`${API}/calls?token=${token}`)
        .then((r) => setCalls(r.data))
        .catch((e) => console.error('[Calling] calls failed:', e)),
      axios.get(`${API}/contacts?token=${token}`)
        .then((r) => {
          const contactsData = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
          setContacts(contactsData);
        })
        .catch((e) => console.error('[Calling] contacts failed:', e)),
      axios.get(`${API}/integrations/status?token=${token}`)
        .then((r) => {
          setTwilioStatus(r.data.twilio);
          setElevenLabsStatus(r.data.elevenlabs);
        })
        .catch((e) => console.error('[Calling] integrations status failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const syncCallToHubSpot = async (callSid) => {
    setSyncingCallId(callSid);
    try {
      const response = await axios.post(
        `${API}/integrations/hubspot/sync-call/${callSid}?token=${token}`
      );
      
      if (response.data.success) {
        toast.success('Call synced to HubSpot successfully');
        // Refresh calls to update the UI
        const callsRes = await axios.get(`${API}/calls?token=${token}`);
        setCalls(callsRes.data);
      } else {
        toast.error(response.data.error || 'Failed to sync call to HubSpot');
      }
    } catch (error) {
      console.error('Failed to sync call to HubSpot:', error);
      const message = error.response?.data?.detail || error.response?.data?.error || 'Failed to sync call';
      toast.error(message);
    } finally {
      setSyncingCallId(null);
    }
  };

  const startCall = async () => {
    if (!phoneNumber && !selectedContact) {
      toast.error('Please enter a phone number or select a contact');
      return;
    }

    const numberToCall = phoneNumber || selectedContact?.phone;
    if (!numberToCall) {
      toast.error('No phone number available');
      return;
    }

    setCallStatus('dialing');
    setIsCallActive(true);
    setShowDialer(false);

    try {
      const response = await axios.post(`${API}/integrations/twilio/call?token=${token}`, {
        to_number: numberToCall,
        contact_id: selectedContact?.id
      });

      if (response.data.success) {
        setCurrentCall({
          sid: response.data.call_sid,
          to: numberToCall,
          from: response.data.from_number,
          status: response.data.status,
          contact: selectedContact
        });
        setCallStatus('connected');
        toast.success(`Call initiated to ${numberToCall}`);
        
        // Start duration timer
        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      } else {
        throw new Error(response.data.error || 'Call failed');
      }
    } catch (error) {
      console.error('Failed to start call', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to initiate call');
      setIsCallActive(false);
      setCallStatus('idle');
    }
  };

  const endCall = async () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    
    setCallStatus('ended');
    toast.info('Call ended');
    
    // Log the call
    if (currentCall) {
      try {
        await axios.post(`${API}/calls?token=${token}`, {
          phone_number: currentCall.to,
          direction: 'outbound',
          duration: callDuration,
          status: 'completed',
          contact_id: currentCall.contact?.id,
          twilio_call_sid: currentCall.sid
        });
        fetchData(); // Refresh call history
      } catch (error) {
        console.error('Failed to log call', error);
      }
    }
    
    setTimeout(() => {
      setIsCallActive(false);
      setCallStatus('idle');
      setCurrentCall(null);
      setCallDuration(0);
      setPhoneNumber('');
      setSelectedContact(null);
    }, 1000);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDurationLong = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'positive') return 'text-green-500';
    if (sentiment === 'negative') return 'text-red-500';
    return 'text-yellow-500';
  };

  const filteredContacts = contacts.filter(c => 
    c.phone && (
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone?.includes(searchQuery)
    )
  );

  const handleDialpadPress = (digit) => {
    setPhoneNumber(prev => prev + digit);
  };

  const handlePlayRecording = async (call) => {
    try {
      const callSid = call.twilio_call_sid || call.call_sid;
      if (!callSid) {
        toast.info('No recording available for this call');
        return;
      }

      // If already playing this call, toggle pause/play
      if (playingRecordingSid === callSid && showAudioPlayer) {
        if (isAudioPlaying) {
          audioRef.current?.pause();
          setIsAudioPlaying(false);
        } else {
          audioRef.current?.play();
          setIsAudioPlaying(true);
        }
        return;
      }

      // Fetch recordings
      const response = await axios.get(`${API}/integrations/twilio/calls/${callSid}/recordings?token=${token}`);
      const recordings = response.data.recordings || [];

      if (recordings.length === 0) {
        toast.info('No recording available for this call yet. Recordings are processed after the call ends.');
        return;
      }

      // Open audio player
      setPlayingRecordingSid(callSid);
      setCurrentAudioCall(call);
      setShowAudioPlayer(true);
      setAudioCurrentTime(0);
      setPlaybackSpeed(1);
      
      const audioUrl = `${API}/integrations/twilio/recordings/${recordings[0].sid}/audio?token=${token}`;
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.playbackRate = 1;
        audioRef.current.play();
        setIsAudioPlaying(true);
      }
    } catch (error) {
      console.error('Failed to play recording', error);
      toast.error('Failed to play recording');
      setPlayingRecordingSid(null);
      setShowAudioPlayer(false);
    }
  };

  const handleCloseAudioPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setShowAudioPlayer(false);
    setPlayingRecordingSid(null);
    setCurrentAudioCall(null);
    setIsAudioPlaying(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
  };

  const handleSeek = (value) => {
    const newTime = value[0];
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setAudioCurrentTime(newTime);
    }
  };

  const handleSkipForward = (seconds = 10) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.currentTime + seconds, audioDuration);
    }
  };

  const handleSkipBack = (seconds = 10) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(audioRef.current.currentTime - seconds, 0);
    }
  };

  const handleSpeedChange = () => {
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const IntegrationStatus = () => (
    <div className="flex items-center gap-4 mb-4">
      <div className="flex items-center gap-2">
        <Phone className="w-4 h-4" />
        <span className="text-sm">Twilio:</span>
        {twilioStatus?.configured ? (
          <Badge className="bg-green-500 text-white text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        ) : (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Configured
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Volume2 className="w-4 h-4" />
        <span className="text-sm">ElevenLabs:</span>
        {elevenLabsStatus?.configured ? (
          <Badge className="bg-green-500 text-white text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        ) : (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Configured
          </Badge>
        )}
      </div>
      {twilioStatus?.phone_number && (
        <div className="text-sm text-slate-500">
          Your number: <span className="font-mono">{twilioStatus.phone_number}</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="calling-loading">
        <p className="text-slate-500">Loading AI Calling Hub...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calling-page">
      {/* Hidden audio element for recording playback */}
      <audio 
        ref={audioRef} 
        onEnded={() => {
          setIsAudioPlaying(false);
          setAudioCurrentTime(0);
        }}
        onError={() => {
          toast.error('Failed to load audio');
          handleCloseAudioPlayer();
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setAudioCurrentTime(audioRef.current.currentTime);
          }
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            setAudioDuration(audioRef.current.duration);
          }
        }}
        style={{ display: 'none' }}
      />

      {/* Audio Player Modal */}
      {showAudioPlayer && currentAudioCall && (
        <Card className="fixed bottom-4 right-4 w-96 bg-slate-900 text-white border-0 shadow-2xl z-50" data-testid="audio-player">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium text-sm">{currentAudioCall.phone_number}</p>
                <p className="text-xs text-slate-400">
                  {currentAudioCall.contact_name || 'Call Recording'}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleCloseAudioPlayer}
                className="text-slate-400 hover:text-white h-6 w-6 p-0"
              >
                ✕
              </Button>
            </div>
            
            {/* Progress bar */}
            <div className="mb-3">
              <Slider
                value={[audioCurrentTime]}
                max={audioDuration || 100}
                step={0.1}
                onValueChange={handleSeek}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{formatTime(audioCurrentTime)}</span>
                <span>{formatTime(audioDuration)}</span>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-center gap-2">
              {/* Skip back 10s */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleSkipBack(10)}
                className="text-slate-300 hover:text-white h-8 w-8 p-0"
                title="Back 10s"
              >
                <SkipBack size={16} />
              </Button>
              
              {/* Play/Pause */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  if (isAudioPlaying) {
                    audioRef.current?.pause();
                    setIsAudioPlaying(false);
                  } else {
                    audioRef.current?.play();
                    setIsAudioPlaying(true);
                  }
                }}
                className="bg-accent hover:bg-accent/80 text-white h-10 w-10 p-0 rounded-full"
              >
                {isAudioPlaying ? <Pause size={20} /> : <Play size={20} />}
              </Button>
              
              {/* Skip forward 10s */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleSkipForward(10)}
                className="text-slate-300 hover:text-white h-8 w-8 p-0"
                title="Forward 10s"
              >
                <SkipForward size={16} />
              </Button>
              
              {/* Playback speed */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSpeedChange}
                className="ml-2 text-xs h-7 px-2 border-slate-600 text-slate-300 hover:text-white"
                title="Change playback speed"
              >
                {playbackSpeed}x
              </Button>
            </div>
            
            {/* Speed options hint */}
            <p className="text-center text-xs text-slate-500 mt-2">
              Click speed button to cycle: 1x → 1.25x → 1.5x → 1.75x → 2x
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Active Call Interface */}
      <Card className="bg-slate-900 text-white border-0 shadow-xl" data-testid="active-call-interface">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <div>AI Calling Hub</div>
              <p className="text-sm font-normal text-slate-400 mt-1">Voice-powered sales automation with Twilio</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <IntegrationStatus />
          
          {!isCallActive ? (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">
                    {twilioStatus?.configured ? 'Ready to make calls' : 'Configure Twilio in Settings'}
                  </span>
                </div>
                <p className="text-sm text-slate-300 max-w-md mx-auto">
                  Make outbound calls with real-time transcription and AI-powered insights
                </p>
              </div>
              <Button
                onClick={() => setShowDialer(true)}
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white"
                disabled={!twilioStatus?.configured}
                data-testid="open-dialer-button"
              >
                <Phone className="mr-2" />
                Open Dialer
              </Button>
              {!twilioStatus?.configured && (
                <p className="text-xs text-yellow-400 mt-3">
                  Please configure Twilio credentials in Settings → Integrations
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8" data-testid="active-call-panel">
              <div className="mb-6">
                <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  callStatus === 'dialing' ? 'bg-yellow-500 animate-pulse' :
                  callStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                  'bg-red-500'
                }`}>
                  {callStatus === 'ended' ? (
                    <PhoneOff className="w-12 h-12" />
                  ) : (
                    <Phone className="w-12 h-12" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-1">
                  {callStatus === 'dialing' ? 'Dialing...' :
                   callStatus === 'connected' ? 'Call Connected' :
                   'Call Ended'}
                </h3>
                <p className="text-lg font-mono text-slate-300">{currentCall?.to}</p>
                {currentCall?.contact && (
                  <p className="text-sm text-slate-400">{currentCall.contact.name}</p>
                )}
                {callStatus === 'connected' && (
                  <div className="mt-4">
                    <div className="text-3xl font-mono text-green-400">
                      {formatDuration(callDuration)}
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Activity className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400">Live Call</span>
                    </div>
                  </div>
                )}
              </div>
              
              {callStatus !== 'ended' && (
                <Button
                  onClick={endCall}
                  size="lg"
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="end-call-button"
                >
                  <PhoneOff className="mr-2" />
                  End Call
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="kpi-card" data-testid="kpi-total-calls">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Calls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading font-light text-primary">{calls.length}</div>
            <p className="text-xs text-slate-500 mt-1">All time</p>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-avg-duration">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Avg Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading font-light text-primary">
              {calls.length > 0 
                ? formatDurationLong(Math.round(calls.reduce((a, c) => a + (c.duration || 0), 0) / calls.length))
                : 'N/A'
              }
            </div>
            <p className="text-xs text-slate-500 mt-1">Per call</p>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-outbound">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Outbound Calls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading font-light text-primary">
              {calls.filter(c => c.direction === 'outbound').length}
            </div>
            <p className="text-xs text-slate-500 mt-1">This month</p>
          </CardContent>
        </Card>
      </div>

      {/* Call History */}
      <Card data-testid="call-history">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Call History</CardTitle>
        </CardHeader>
        <CardContent>
          {calls.length > 0 ? (
            <div className="space-y-3">
              {calls.map((call) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                  data-testid={`call-history-item-${call.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        call.direction === 'inbound' ? 'bg-blue-100' : 'bg-green-100'
                      }`}
                    >
                      {call.direction === 'inbound' ? (
                        <PhoneIncoming size={18} className="text-blue-600" />
                      ) : (
                        <PhoneOutgoing size={18} className="text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-primary">{call.phone_number}</p>
                      <p className="text-xs text-slate-500">
                        {call.contact_name || (call.contact_id ? 'Linked contact' : 'Unknown contact')}
                      </p>
                      {call.created_at && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(call.created_at).toLocaleDateString('en-US', {
                            timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Duration badge - prominent display */}
                    <div className="flex flex-col items-center px-3 py-1 bg-slate-100 rounded-lg min-w-[70px]">
                      <span className="text-lg font-semibold text-primary">
                        {call.duration > 0 ? formatDurationLong(call.duration) : '--:--'}
                      </span>
                      <span className="text-xs text-slate-500">duration</span>
                    </div>
                    
                    {/* Play button */}
                    {(call.twilio_call_sid || call.call_sid) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePlayRecording(call)}
                        className={`h-9 px-3 gap-1 ${
                          playingRecordingSid === (call.twilio_call_sid || call.call_sid) 
                            ? 'bg-red-50 border-red-300 text-red-600' 
                            : 'hover:bg-green-50 hover:border-green-300'
                        }`}
                        data-testid={`play-call-${call.id}`}
                      >
                        {playingRecordingSid === (call.twilio_call_sid || call.call_sid) ? (
                          <>
                            <Pause size={14} />
                            <span className="text-xs">Playing</span>
                          </>
                        ) : (
                          <>
                            <Play size={14} />
                            <span className="text-xs">Play</span>
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* Status badge */}
                    <Badge
                      className={`text-xs ${
                        call.status === 'completed' ? 'bg-green-500' : 
                        call.status === 'failed' ? 'bg-red-500' :
                        call.status === 'busy' ? 'bg-yellow-500' :
                        'bg-slate-400'
                      }`}
                    >
                      {call.status}
                    </Badge>
                    
                    {/* HubSpot Sync Indicator/Button */}
                    {call.status === 'completed' && (
                      call.hubspot_synced ? (
                        <div 
                          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-600"
                          title={`Synced to HubSpot${call.hubspot_call_id ? ` (ID: ${call.hubspot_call_id})` : ''}`}
                        >
                          <Cloud size={12} className="text-orange-500" />
                          <span className="hidden sm:inline">HubSpot</span>
                        </div>
                      ) : call.contact_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => syncCallToHubSpot(call.twilio_call_sid)}
                          disabled={syncingCallId === call.twilio_call_sid}
                          className="h-7 px-2 text-xs text-slate-500 hover:text-orange-600 hover:bg-orange-50"
                          title="Sync this call to HubSpot"
                        >
                          {syncingCallId === call.twilio_call_sid ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <CloudOff size={12} />
                          )}
                          <span className="hidden sm:inline ml-1">Sync</span>
                        </Button>
                      ) : (
                        <div 
                          className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-400"
                          title="No contact linked - cannot sync to HubSpot"
                        >
                          <CloudOff size={12} />
                        </div>
                      )
                    )}
                    
                    {call.sentiment && (
                      <span className={`text-xs font-semibold ${getSentimentColor(call.sentiment)}`}>
                        {call.sentiment}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="call-history-empty">
              <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No call history yet. Start making calls to see them here.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialer Dialog */}
      <Dialog open={showDialer} onOpenChange={setShowDialer}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Make a Call
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Phone Number Input */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Phone Number
              </label>
              <Input
                type="tel"
                placeholder="+1234567890"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="text-lg font-mono"
                data-testid="phone-input"
              />
            </div>

            {/* Dialpad */}
            <div className="grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                <Button
                  key={digit}
                  variant="outline"
                  className="h-12 text-xl font-semibold"
                  onClick={() => handleDialpadPress(digit)}
                >
                  {digit}
                </Button>
              ))}
            </div>

            {/* Or select from contacts */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Or select a contact
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredContacts.slice(0, 5).map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => {
                      setSelectedContact(contact);
                      setPhoneNumber(contact.phone);
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedContact?.id === contact.id 
                        ? 'bg-accent/10 border border-accent' 
                        : 'hover:bg-slate-100 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{contact.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{contact.phone}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredContacts.length === 0 && searchQuery && (
                  <p className="text-sm text-slate-500 text-center py-4">No contacts found with phone numbers</p>
                )}
              </div>
            </div>

            {/* Call Button */}
            <Button
              onClick={startCall}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              size="lg"
              disabled={!phoneNumber && !selectedContact}
              data-testid="dial-button"
            >
              <Phone className="mr-2" />
              Call {phoneNumber || selectedContact?.phone || ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calling;
